import React, { useEffect, useRef, useState } from "react";
import { useSpring, animated } from "react-spring";

import emailjs from "emailjs-com";
import Swal from "sweetalert2";

import useForm from "../utils/hooks/useForm";
import validate from "../utils/validations/ContactFormRules";

const {
  REACT_APP_EMAILJS_SERVICE_ID,
  REACT_APP_EMAILJS_TEMPLATE_ID,
  REACT_APP_EMAILJS_PUBLIC_KEY,
  REACT_APP_TURNSTILE_SITE_KEY,
} = process.env;

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export default function Contact() {
  const widgetContainerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState("");

  const fadeIn = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    config: {
      delay: 4000,
      duration: 800,
    },
  });

  const openAlert = () => {
    Swal.fire({
      title: "Message received. Thanks!",
      text: "I'll be in touch with you shortly.",
      icon: "success",
      timer: "4000",
      confirmButtonColor: "#1c273b",
      confirmButtonText: "Yes!",
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.reload();
      }
    });
  };

  const sendEmail = () => {
    let templateParams = {
      from_name: values.name,
      message: values.message,
      from_email: values.email,
    };

    emailjs
      .send(
        REACT_APP_EMAILJS_SERVICE_ID,
        REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(
        function (response) {
          if (process.env.NODE_ENV === "development") {
            console.log("SUCCESS!", response.status, response.text);
          }
          openAlert();
        },
        function (error) {
          if (process.env.NODE_ENV === "development") {
            console.error("ERROR!", error);
          }
        }
      );
  };

  const submitForm = () => {
    sendEmail();
    resetForm();
  };

  const validateWithTurnstile = (formValues) =>
    validate({ ...formValues, turnstileToken });

  const { values, errors, handleChange, handleSubmit, handleReset } = useForm(
    submitForm,
    validateWithTurnstile
  );

  const resetTurnstile = () => {
    setTurnstileToken("");
    if (window.turnstile && widgetIdRef.current !== null) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  const resetForm = () => {
    handleReset();
    resetTurnstile();
  };

  useEffect(() => {
    if (!REACT_APP_TURNSTILE_SITE_KEY || !widgetContainerRef.current) {
      return undefined;
    }

    const renderWidget = () => {
      if (!window.turnstile || !widgetContainerRef.current) {
        return;
      }

      if (widgetIdRef.current !== null) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
        sitekey: REACT_APP_TURNSTILE_SITE_KEY,
        callback: function (token) {
          setTurnstileToken(token);
        },
        "expired-callback": function () {
          setTurnstileToken("");
        },
        "error-callback": function () {
          setTurnstileToken("");
        },
      });
    };

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);

    if (window.turnstile) {
      renderWidget();
    } else if (existingScript) {
      existingScript.addEventListener("load", renderWidget);
    } else {
      const script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", renderWidget);
      document.body.appendChild(script);
    }

    return () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  return (
    <div id="wrapper">
      <div id="main">
        <section id="four">
          <animated.div style={fadeIn} className="container">
            <h3>Get in Touch</h3>
            <p>If you want to contact me, feel free to shot me an email!</p>
            <form onSubmit={handleSubmit} autoComplete="on">
              <div className="row gtr-uniform">
                <div className="col-6 col-12-xsmall">
                  <input
                    className={`input ${errors.name && "danger"}`}
                    type="text"
                    name="name"
                    id="name"
                    placeholder="Name"
                    onChange={handleChange}
                    value={values.name || ""}
                    required
                  />
                  {errors.name && <p className="help danger">{errors.name}</p>}
                </div>
                <div className="col-6 col-12-xsmall">
                  <input
                    className={`input ${errors.email && "danger"}`}
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Email"
                    onChange={handleChange}
                    value={values.email || ""}
                    required
                  />
                  {errors.email && (
                    <p className="help danger">{errors.email}</p>
                  )}
                </div>
                <div className="col-12">
                  <textarea
                    className={`input ${errors.message && "danger"}`}
                    name="message"
                    id="message"
                    placeholder="Message"
                    rows="6"
                    onChange={handleChange}
                    value={values.message || ""}
                    required
                  ></textarea>
                  {errors.message && (
                    <p className="help danger">{errors.message}</p>
                  )}
                </div>
                <div className="col-12">
                  <div ref={widgetContainerRef} />
                  {errors.turnstile && (
                    <p className="help danger">{errors.turnstile}</p>
                  )}
                </div>
                <div className="col-12">
                  <ul className="actions">
                    <li>
                      <input
                        type="submit"
                        className="primary"
                        value="Send Message"
                      />
                    </li>
                    <li>
                      <input
                        type="reset"
                        value="Reset Form"
                        onClick={resetForm}
                      />
                    </li>
                  </ul>
                </div>
              </div>
            </form>
          </animated.div>
        </section>
      </div>
    </div>
  );
}
